/**
 * Bible Mysteries Decoder — w02_luther, per-production overlay composition.
 * Generated by bd_overlay_gen.py — anchors resolved against
 * productions.word_timestamps_hindi (English words), in script order.
 *
 * Devotional route: NO logo here (ffmpeg adds it from channel_config).
 * All times are S(seconds) so the comp is correct at 16fps or 24fps.
 * 47 events (FactTag = citation/date/person/place/manuscript; BigMoment = reveal/verdict/myth-bust).
 */
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { FactTag, BigMoment } from "./BibleDecoderOverlayFinal";
import { loadFont as loadMono } from "@remotion/google-fonts/IBMPlexMono";

const { fontFamily: mono } = loadMono("normal", { weights: ["400", "500"], subsets: ["latin"] });
const TEXT_SHADOW = "0 2px 6px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.9), 0 0 22px rgba(0,0,0,0.55)";

const Wordmark: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const fade = interpolate(frame, [0, Math.round(2.25 * fps)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div
      style={{
        position: "absolute", bottom: 18, right: 46.67,
        fontFamily: mono, fontSize: 11, letterSpacing: 2.4, fontWeight: 500,
        color: "#ffffff", textTransform: "uppercase",
        textShadow: TEXT_SHADOW, opacity: fade,
      }}
    >
      Bible Mysteries Decoder
    </div>
  );
};

const Hold: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = Math.round(0.6 * fps);
  const out = interpolate(frame, [dur - f, dur], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ opacity: out }}>{children}</AbsoluteFill>;
};

export const BibleDecoderW02Overlays: React.FC = () => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      <Wordmark />
      {/*  1 · FactTag · THE CLAIM · anchor: says three books */}
      <Sequence from={S(1.382)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="THE CLAIM" value="Luther cut 3 books" />
        </Hold>
      </Sequence>
      {/*  2 · FactTag · THE REAL FIGHT · anchor: which books get to call themselves scripture */}
      <Sequence from={S(11.227)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="THE REAL FIGHT" value="A 1,500-year canon war" />
        </Hold>
      </Sequence>
      {/*  3 · FactTag · CITATION · anchor: chapter seven */}
      <Sequence from={S(35.112)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="CITATION" value="2 Maccabees 7" />
        </Hold>
      </Sequence>
      {/*  4 · BigMoment · THE MARTYRS · anchor: will give their bodies back */}
      <Sequence from={S(58.994)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE MARTYRS" headline="A mother and seven sons" sub="Tortured to death one by one, then told they would live again" />
        </Hold>
      </Sequence>
      {/*  5 · BigMoment · THE MYTH · anchor: took a knife to the Bible */}
      <Sequence from={S(98.239)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE MYTH" headline="Luther took a knife to the Bible" sub="Cut three books out and erased them. The story in a thousand videos." />
        </Hold>
      </Sequence>
      {/*  6 · BigMoment · THE FACT · anchor: did not erase a single book */}
      <Sequence from={S(111.971)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE FACT" headline="He erased nothing" sub="Not one book. He didn't have the power, and he didn't try" />
        </Hold>
      </Sequence>
      {/*  7 · FactTag · CITATION · anchor: King James Bible in the year sixteen eleven */}
      <Sequence from={S(128.922)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="CITATION" value="King James Bible · 1611" />
        </Hold>
      </Sequence>
      {/*  8 · BigMoment · WHAT HE ACTUALLY DID · anchor: He moved them. */}
      <Sequence from={S(137.827)} durationInFrames={S(8.000)}>
        <Hold dur={S(8.000)}>
          <BigMoment kicker="WHAT HE ACTUALLY DID" headline="He moved them" sub="Lifted a stack of books out of the sequence into a section of their own" />
        </Hold>
      </Sequence>
      {/*  9 · FactTag · THE REAL TARGET · anchor: biggest problem with were in the New Testament */}
      <Sequence from={S(166.537)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="THE REAL TARGET" value="4 New Testament books" />
        </Hold>
      </Sequence>
      {/* 10 · FactTag · MANUSCRIPT · anchor: lump together as the Septuagint */}
      <Sequence from={S(191.684)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="MANUSCRIPT" value="The Septuagint · Greek Old Testament" />
        </Hold>
      </Sequence>
      {/* 11 · BigMoment · THE DISPUTED BOOKS · anchor: Tobit. Judith. Wisdom. Sirach. */}
      <Sequence from={S(200.078)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE DISPUTED BOOKS" headline="Tobit · Judith · Wisdom · Sirach · Maccabees" sub="Carried by the Greek, never in the Hebrew Bible the rabbis used" />
        </Hold>
      </Sequence>
      {/* 12 · FactTag · PERSON · anchor: the scholar who translated the Latin Bible */}
      <Sequence from={S(215.311)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="PERSON" value="Jerome · translator of the Latin Bible" />
        </Hold>
      </Sequence>
      {/* 13 · BigMoment · THE LABEL · anchor: He called those extra books apocrypha */}
      <Sequence from={S(219.973)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE LABEL" headline="Apocrypha: hidden, put away" sub="Not false, not forgeries. A verdict compressed into one word" />
        </Hold>
      </Sequence>
      {/* 14 · FactTag · PERSON · anchor: Augustine, disagreed and kept them */}
      <Sequence from={S(229.841)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="PERSON" value="Augustine · kept them in" />
        </Hold>
      </Sequence>
      {/* 15 · FactTag · JEROME'S IRONY · anchor: he translated two of them anyway */}
      <Sequence from={S(336.861)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="JEROME'S IRONY" value="Translated Tobit & Judith anyway" />
        </Hold>
      </Sequence>
      {/* 16 · FactTag · PERSON · anchor: Luther was a monk in Germany, born fourteen eighty three */}
      <Sequence from={S(372.156)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="PERSON" value="Martin Luther · b. 1483, Germany" />
        </Hold>
      </Sequence>
      {/* 17 · FactTag · THE PRACTICE · anchor: The practice was called indulgences */}
      <Sequence from={S(386.018)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="THE PRACTICE" value="Indulgences" />
        </Hold>
      </Sequence>
      {/* 18 · FactTag · DATE · anchor: In fifteen seventeen he sent a list of objections */}
      <Sequence from={S(406.046)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="DATE" value="1517 · his list of objections" />
        </Hold>
      </Sequence>
      {/* 19 · FactTag · DATE · anchor: In fifteen twenty one they hauled him */}
      <Sequence from={S(426.526)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="DATE" value="1521 · the Diet of Worms" />
        </Hold>
      </Sequence>
      {/* 20 · BigMoment · AT WORMS · anchor: my conscience is held captive */}
      <Sequence from={S(442.791)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="AT WORMS" headline="“My conscience is captive to the word of God”" sub="Recant, or be branded an outlaw any man could kill. He refused." />
        </Hold>
      </Sequence>
      {/* 21 · FactTag · DATE · anchor: he printed it, in fifteen twenty two */}
      <Sequence from={S(459.835)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="DATE" value="1522 · the German New Testament" />
        </Hold>
      </Sequence>
      {/* 22 · FactTag · CITATION · anchor: Second Maccabees, chapter twelve */}
      <Sequence from={S(519.114)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="CITATION" value="2 Maccabees 12" />
        </Hold>
      </Sequence>
      {/* 23 · BigMoment · THE VERSE · anchor: holy and good thing to pray for the dead */}
      <Sequence from={S(533.37)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE VERSE" headline="“A holy and good thing to pray for the dead”" sub="The sharpest scriptural support for purgatory, and it stood on contested ground" />
        </Hold>
      </Sequence>
      {/* 24 · BigMoment · THE COLLISION · anchor: The book and the man could not both stand */}
      <Sequence from={S(563.846)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE COLLISION" headline="The book and the man could not both stand" sub="Purgatory's clearest verse sat inside a book Luther disputed" />
        </Hold>
      </Sequence>
      {/* 25 · FactTag · DATE · anchor: In fifteen thirty four Luther finished translating the whole Bible */}
      <Sequence from={S(573.464)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="DATE" value="1534 · the complete German Bible" />
        </Hold>
      </Sequence>
      {/* 26 · BigMoment · THE HEADER HE WROTE · anchor: which are not held equal to the holy scriptures */}
      <Sequence from={S(600.585)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE HEADER HE WROTE" headline="“Not equal to holy scripture, yet useful and good to read”" sub="He kept them, printed them, bound them, and told you where they ranked" />
        </Hold>
      </Sequence>
      {/* 27 · BigMoment · THE REAL NUMBER · anchor: Luther's own Bible moved seven */}
      <Sequence from={S(633.953)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE REAL NUMBER" headline="Seven, not three" sub="Plus extra chapters cut from Daniel and Esther, and the Prayer of Manasseh" />
        </Hold>
      </Sequence>
      {/* 28 · FactTag · JUDITH · anchor: a general named Holofernes */}
      <Sequence from={S(679.229)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="JUDITH" value="Beheads the general Holofernes" />
        </Hold>
      </Sequence>
      {/* 29 · FactTag · TOBIT · anchor: a healing fish and an archangel */}
      <Sequence from={S(687.705)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="TOBIT" value="A healing fish & a hidden angel" />
        </Hold>
      </Sequence>
      {/* 30 · BigMoment · HANUKKAH · anchor: the story behind that festival is not in the Hebrew Bible */}
      <Sequence from={S(718.103)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="HANUKKAH" headline="Runs on a “removed” book" sub="The festival's story isn't in the Hebrew Bible. It's in First Maccabees." />
        </Hold>
      </Sequence>
      {/* 31 · FactTag · WISDOM · anchor: It speaks of the soul as immortal */}
      <Sequence from={S(738.766)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="WISDOM" value="Speaks of the soul as immortal" />
        </Hold>
      </Sequence>
      {/* 32 · BigMoment · THE HUMAN COST · anchor: that mother and her seven sons went with it */}
      <Sequence from={S(780.411)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="THE HUMAN COST" headline="The mother and her seven sons went with it" sub="When Second Maccabees moved to the back, they were shelved too" />
        </Hold>
      </Sequence>
      {/* 33 · BigMoment · HIS MEASURING STICK · anchor: whatever urges Christ */}
      <Sequence from={S(883.281)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="HIS MEASURING STICK" headline="“Whatever urges Christ”" sub="A book mattered to the degree it preached Christ, so even the famous ones ranked lower" />
        </Hold>
      </Sequence>
      {/* 34 · FactTag · THE FOUR HE DEMOTED · anchor: Four books ranked low by that measure */}
      <Sequence from={S(893.568)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="THE FOUR HE DEMOTED" value="Hebrews · James · Jude · Revelation" />
        </Hold>
      </Sequence>
      {/* 35 · BigMoment · IN PRINT · anchor: an epistle of straw, compared */}
      <Sequence from={S(927.307)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="IN PRINT" headline="James: “an epistle of straw”" sub="James says faith without works is dead. Luther had staked everything on faith alone." />
        </Hold>
      </Sequence>
      {/* 36 · FactTag · PERSON · anchor: a church historian named Eusebius */}
      <Sequence from={S(964.674)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="PERSON" value="Eusebius · church historian, c. 320 AD" />
        </Hold>
      </Sequence>
      {/* 37 · BigMoment · ALREADY DISPUTED · anchor: in his disputed pile */}
      <Sequence from={S(977.55)} durationInFrames={S(9.000)}>
        <Hold dur={S(9.000)}>
          <BigMoment kicker="ALREADY DISPUTED" headline="The doubts were centuries old" sub="Eusebius filed James, Jude and 2 Peter as disputed 1,200 years before Luther" />
        </Hold>
      </Sequence>
      {/* 38 · BigMoment · IT DIDN'T STICK · anchor: all twenty seven books, fully canonical */}
      <Sequence from={S(1039.185)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="IT DIDN'T STICK" headline="All 27 New Testament books stayed" sub="James, Hebrews, Jude and Revelation stayed fully canonical. His verdict never became a rule." />
        </Hold>
      </Sequence>
      {/* 39 · FactTag · DATE · anchor: removed by Bible printing societies in the eighteen twenties */}
      <Sequence from={S(1100.12)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="DATE" value="1820s · printers dropped them" />
        </Hold>
      </Sequence>
      {/* 40 · FactTag · THE FOUNDATION · anchor: balanced on one phrase, scripture alone */}
      <Sequence from={S(1125.123)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="THE FOUNDATION" value="Sola scriptura: scripture alone" />
        </Hold>
      </Sequence>
      {/* 41 · BigMoment · THE TRAPDOOR · anchor: you cannot use the Bible to settle what counts as the Bible */}
      <Sequence from={S(1148.796)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE TRAPDOOR" headline="The table of contents isn't printed inside the book" sub="You cannot use the Bible to settle what counts as the Bible" />
        </Hold>
      </Sequence>
      {/* 42 · FactTag · DATE · anchor: fifteen forty six, at the Council of Trent */}
      <Sequence from={S(1219.639)} durationInFrames={S(6.000)}>
        <Hold dur={S(6.000)}>
          <FactTag kicker="DATE" value="1546 · the Council of Trent" />
        </Hold>
      </Sequence>
      {/* 43 · BigMoment · TRENT'S ANSWER · anchor: attach a curse, a formal anathema */}
      <Sequence from={S(1261.092)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="TRENT'S ANSWER" headline="Disputed books declared scripture, under anathema" sub="For the first time in 1,500 years, Rome sealed its Old Testament. It was reacting to Luther." />
        </Hold>
      </Sequence>
      {/* 44 · BigMoment · THREE BIBLES TODAY · anchor: a Catholic Bible has seventy three books */}
      <Sequence from={S(1324.763)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THREE BIBLES TODAY" headline="Catholic 73 · Protestant 66 · Ethiopian 80+" sub="Three tables of contents, three answers, all called the Bible" />
        </Hold>
      </Sequence>
      {/* 45 · FactTag · ETHIOPIA · anchor: the book of Enoch that the rest of the world */}
      <Sequence from={S(1346.863)} durationInFrames={S(7.000)}>
        <Hold dur={S(7.000)}>
          <FactTag kicker="ETHIOPIA" value="Still keeps the Book of Enoch" />
        </Hold>
      </Sequence>
      {/* 46 · BigMoment · THE VERDICT · anchor: He moved seven, not three */}
      <Sequence from={S(1366.278)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="THE VERDICT" headline="Moved seven. Erased none." sub="He kept them in print with a label. The books didn't disappear. The certainty did." />
        </Hold>
      </Sequence>
      {/* 47 · BigMoment · BIBLE MYSTERIES DECODER · anchor: one buried claim at a time, until */}
      <Sequence from={S(1479.224)} durationInFrames={S(10.000)}>
        <Hold dur={S(10.000)}>
          <BigMoment kicker="BIBLE MYSTERIES DECODER" headline="One buried claim at a time" sub="Until the story you were handed and the story in the records are the same story" />
        </Hold>
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
